import React, { useCallback, useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { SupervisorTeamMember } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  ClinicianRolePill,
  PortalDataTable,
  PortalTableColumn,
  RelationshipPillList,
  StatusPill,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { TeamTabs } from "../components/PortalNavigation";
import {
  TeamClinicFilter,
  TeamClinicUnavailableState,
} from "../components/TeamClinicFilter";
import { teamClientsPath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import {
  comparePortalText,
  PortalSortDirection,
  stablePortalSort,
} from "../utils/tableSort";
import {
  uniqueSupervisorTeamMembers,
  useTeamClinicScope,
  withTeamClinicScope,
} from "../utils/teamClinicScope";

type TeamMemberSortKey =
  | "therapistName"
  | "role"
  | "email"
  | "clinicianCode"
  | "clinics"
  | "status";

function isSupervisor(member: SupervisorTeamMember): boolean {
  return member.memberships.some(
    (membership) => membership.membershipRole === "supervisor",
  );
}

function compareTeamMembers(
  left: SupervisorTeamMember,
  right: SupervisorTeamMember,
): number {
  if (left.isCurrentUser !== right.isCurrentUser) {
    return left.isCurrentUser ? -1 : 1;
  }

  const roleDifference =
    Number(isSupervisor(right)) - Number(isSupervisor(left));
  if (roleDifference !== 0) return roleDifference;

  return left.therapistName.localeCompare(right.therapistName, undefined, {
    sensitivity: "base",
  });
}

function teamColumns(options: {
  sortProps: (
    key: TeamMemberSortKey,
  ) => Pick<
    PortalTableColumn<SupervisorTeamMember>,
    "sortDirection" | "onSort"
  >;
}): readonly PortalTableColumn<SupervisorTeamMember>[] {
  return [
    {
      key: "therapistName",
      header: "Clinician",
      initialWidth: 240,
      minWidth: 180,
      ...options.sortProps("therapistName"),
      render: (member) => (
        <span className="cp-team-member-name">
          <strong>{member.therapistName}</strong>
          {member.isCurrentUser ? <small>You</small> : null}
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      initialWidth: 140,
      minWidth: 120,
      ...options.sortProps("role"),
      render: (member) => (
        <div className="cp-pill-list">
          {Array.from(
            new Set(
              member.memberships.map((membership) => membership.membershipRole),
            ),
          ).map((role) => (
            <ClinicianRolePill key={role} role={role} />
          ))}
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      initialWidth: 290,
      minWidth: 220,
      ...options.sortProps("email"),
      render: (member) =>
        member.email || <span className="cp-empty-value">—</span>,
    },
    {
      key: "clinicianCode",
      header: "Clinician Code",
      initialWidth: 150,
      minWidth: 130,
      ...options.sortProps("clinicianCode"),
      render: (member) =>
        member.clinicianCode || <span className="cp-empty-value">—</span>,
    },
    {
      key: "clinics",
      header: "Clinics",
      initialWidth: 320,
      minWidth: 220,
      ...options.sortProps("clinics"),
      render: (member) => (
        <RelationshipPillList
          names={member.memberships.map((membership) => membership.clinicName)}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      initialWidth: 130,
      minWidth: 110,
      resizable: false,
      ...options.sortProps("status"),
      render: (member) => <StatusPill status={member.status} />,
    },
  ];
}

export function TeamOverviewPage({ clinicianCode }: { clinicianCode: string }) {
  const { clinicId, setClinicId } = useTeamClinicScope();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<TeamMemberSortKey | null>(null);
  const [sortDirection, setSortDirection] =
    useState<PortalSortDirection>("ascending");
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamOverview(
        clinicianCode,
        clinicId,
        signal,
      ),
    [clinicId, clinicianCode],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found && data.authorized && data.therapist?.role === "supervisor",
  );
  const clinicOptions = data?.clinicOptions ?? data?.clinics ?? [];
  const staleClinic = Boolean(
    data && clinicId && !clinicOptions.some((clinic) => clinic.id === clinicId),
  );
  const updateSort = (nextSortKey: TeamMemberSortKey): void => {
    setSortDirection(
      nextSortKey === sortKey && sortDirection === "ascending"
        ? "descending"
        : "ascending",
    );
    setSortKey(nextSortKey);
  };
  const sortProps = (key: TeamMemberSortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });
  const columns = teamColumns({ sortProps });
  const sortedTeamMembers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const defaultOrderedMembers = uniqueSupervisorTeamMembers(
      data?.teamMembers ?? [],
    )
      .filter((member) =>
        !query
          ? true
          : [
              member.therapistName,
              member.email,
              member.clinicianCode,
              member.accountRole,
              member.status,
              ...member.memberships.map((membership) => membership.clinicName),
            ]
              .join(" ")
              .toLocaleLowerCase()
              .includes(query),
      )
      .sort(compareTeamMembers);
    if (!sortKey) return defaultOrderedMembers;
    return stablePortalSort(defaultOrderedMembers, (left, right) => {
      switch (sortKey) {
        case "role":
          return comparePortalText(
            left.memberships
              .map((membership) => membership.membershipRole)
              .sort()
              .join(" "),
            right.memberships
              .map((membership) => membership.membershipRole)
              .sort()
              .join(" "),
            sortDirection,
          );
        case "clinics":
          return comparePortalText(
            left.memberships
              .map((membership) => membership.clinicName)
              .sort()
              .join(" "),
            right.memberships
              .map((membership) => membership.clinicName)
              .sort()
              .join(" "),
            sortDirection,
          );
        default:
          return comparePortalText(
            left[sortKey],
            right[sortKey],
            sortDirection,
          );
      }
    });
  }, [data?.teamMembers, search, sortDirection, sortKey]);

  return (
    <ClinicianPortalLayout
      activeGlobalKey="team"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Team Overview"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        clinicianNameLoading={state.status === "loading"}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Home", path: "/clinician-portal" },
              { label: "Team" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        <TeamTabs
          clinicianCode={clinicianCode}
          activeKey="overview"
          clinicId={clinicId}
        />
        {state.status === "loading" ? (
          <LoadingState label="Loading team overview" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Team overview is unavailable")} />
        ) : null}
        {available && data && staleClinic ? (
          <TeamClinicUnavailableState
            onViewAllClinics={() => setClinicId("")}
          />
        ) : null}
        {available && data && !staleClinic ? (
          <section aria-label="Team roster">
            <div className="cp-content-header">
              <div>
                <p>
                  View clinicians who belong to the clinics you supervise.
                  Select a clinician to view their caseload.
                </p>
              </div>
              <strong className="cp-record-count">
                {sortedTeamMembers.length}{" "}
                {sortedTeamMembers.length === 1
                  ? "team member"
                  : "team members"}
              </strong>
            </div>
            <div className="cp-list-toolbar cp-team-overview-filters">
              <div
                className="cp-list-toolbar__filters"
                role="group"
                aria-label="Team overview filters"
              >
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">
                    Search team members
                  </span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search team members"
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </label>
                <TeamClinicFilter
                  clinicOptions={clinicOptions}
                  value={clinicId}
                  compact
                  onChange={setClinicId}
                />
              </div>
              <button
                type="button"
                className="cp-button cp-button--secondary"
                onClick={state.refresh}
                disabled={state.status === "refreshing"}
              >
                <RefreshCw aria-hidden="true" />
                {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
              </button>
            </div>
            <div className="cp-team-summary" aria-label="Team summary">
              <div className="cp-team-summary__metric" data-tone="clinics">
                <span>Supervised Clinics</span>
                <strong>{clinicOptions.length}</strong>
              </div>
              <div className="cp-team-summary__metric" data-tone="team-members">
                <span>Team members</span>
                <strong>{data.summary.teamMemberCount}</strong>
              </div>
              <div className="cp-team-summary__metric" data-tone="therapists">
                <span>Therapists</span>
                <strong>{data.summary.therapistCount}</strong>
              </div>
              <div className="cp-team-summary__metric" data-tone="supervisors">
                <span>Supervisors</span>
                <strong>{data.summary.supervisorCount}</strong>
              </div>
            </div>
            {sortedTeamMembers.length ? (
              <PortalDataTable
                caption="Team roster"
                columns={columns}
                records={sortedTeamMembers}
                getRecordKey={(member) => member.id}
                getRecordOpenLabel={(member) =>
                  `View ${member.therapistName}'s team clients`
                }
                onRecordOpen={(member) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      withTeamClinicScope(
                        `${teamClientsPath}?therapistId=${encodeURIComponent(
                          member.id,
                        )}`,
                        clinicId,
                      ),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <EmptyState
                title={
                  clinicId
                    ? "No team members were found for the selected clinic."
                    : "No team members found"
                }
                description={
                  search
                    ? "No clinicians match the current search."
                    : "No clinicians are currently assigned to your authorized clinics."
                }
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
